import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Link, useHydrated } from "@tanstack/react-router"
import { useAuth } from "@/contexts/AuthContext"
import { useSignOut } from "@/hooks/useAccount"
import { useTranslation } from "react-i18next"
import { Menu, Phone, DollarSign, Waves, Key, CreditCard, Settings, LogOut, User } from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import Logo from "@/components/Logo"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { useLang } from "@/hooks/useLang"

export default function Header() {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const isHydrated = useHydrated()
    const { isAuthenticated } = useAuth()
    const { t } = useTranslation(['app', 'common'])
    const lang = useLang()
    const signOutMutation = useSignOut()

    return (
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="container mx-auto px-4 py-4">
                <nav className="flex items-center justify-between">
                    <Logo />

                    <div className="hidden md:flex items-center space-x-4">
                        <nav className="flex items-center space-x-6">
                            <Link
                                to="/dialer"
                                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                            >
                                {t('navigation.phone')}
                            </Link>
                            <Link
                                to="/$lang/pricing"
                                params={{ lang }}
                                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                            >
                                {t('navigation.pricing')}
                            </Link>
                            <Link
                                to="/$lang/openclaw"
                                params={{ lang }}
                                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                            >
                                {t('navigation.agentApi')}
                            </Link>
                            {isHydrated && isAuthenticated ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline" className="gap-2">
                                            <User className="h-4 w-4" />
                                            {t('navigation.account')}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                        <DropdownMenuItem asChild>
                                            <Link to="/$lang/account" params={{ lang }} className="gap-2">
                                                <User className="h-4 w-4" />
                                                {t('account.tabs.overview')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/$lang/account/api" params={{ lang }} className="gap-2">
                                                <Key className="h-4 w-4" />
                                                {t('account.tabs.agentApi')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/$lang/account/numbers" params={{ lang }} className="gap-2">
                                                <Phone className="h-4 w-4" />
                                                {t('account.tabs.numbers')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/$lang/account/billing" params={{ lang }} className="gap-2">
                                                <CreditCard className="h-4 w-4" />
                                                {t('account.tabs.billing')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/$lang/account/settings" params={{ lang }} className="gap-2">
                                                <Settings className="h-4 w-4" />
                                                {t('account.tabs.settings')}
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            className="gap-2 text-destructive focus:text-destructive"
                                            disabled={signOutMutation.isPending}
                                            onClick={() => signOutMutation.mutate()}
                                        >
                                            <LogOut className="h-4 w-4" />
                                            {t('auth.logout')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <Button variant="outline" asChild>
                                    <Link to="/$lang/account" params={{ lang }}>
                                        {t('navigation.account')}
                                    </Link>
                                </Button>
                            )}
                        </nav>

                        <div className="flex items-center space-x-3">
                            {isHydrated && !isAuthenticated && (
                                <Button asChild>
                                    <Link to="/login">{t('navigation.getStarted')}</Link>
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setIsMobileMenuOpen(true)}
                        >
                            <Menu className="w-5 h-5" />
                        </Button>
                    </div>
                </nav>

                {/* Mobile bottom sheet */}
                <Dialog open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                    <DialogContent className="sm:max-w-md md:hidden fixed bottom-0 top-auto translate-y-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom rounded-t-2xl rounded-b-none border-t border-border border-x-0 border-b-0 max-h-[85vh] p-0">
                        <div className="px-6 pt-6 pb-8">
                            <DialogHeader className="mb-6">
                                <DialogTitle className="text-2xl font-bold">Menu</DialogTitle>
                            </DialogHeader>

                            {/* Navigation items with icons */}
                            <div className="space-y-2 mb-6">
                                <Link
                                    to="/dialer"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-muted transition-colors group"
                                >
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <Phone className="h-5 w-5 text-primary" />
                                    </div>
                                    <span className="text-lg font-medium">{t('navigation.phone')}</span>
                                </Link>

                                <Link
                                    to="/$lang/pricing"
                                    params={{ lang }}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-muted transition-colors group"
                                >
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <DollarSign className="h-5 w-5 text-primary" />
                                    </div>
                                    <span className="text-lg font-medium">{t('navigation.pricing')}</span>
                                </Link>

                                <Link
                                    to="/$lang/openclaw"
                                    params={{ lang }}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="flex items-center gap-4 px-4 py-4 rounded-lg hover:bg-muted transition-colors group"
                                >
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <Waves className="h-5 w-5 text-primary" />
                                    </div>
                                    <span className="text-lg font-medium">{t('navigation.agentApi')}</span>
                                </Link>

                            </div>

                            {/* Action buttons */}
                            <div className="space-y-3 pt-4 border-t">
                                {isHydrated && isAuthenticated ? (
                                    <>
                                        <Button asChild variant="outline" size="lg" className="w-full">
                                            <Link to="/$lang/account" params={{ lang }} onClick={() => setIsMobileMenuOpen(false)}>
                                                {t('navigation.account')}
                                            </Link>
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="lg"
                                            className="w-full text-destructive hover:text-destructive"
                                            disabled={signOutMutation.isPending}
                                            onClick={() => {
                                                signOutMutation.mutate()
                                                setIsMobileMenuOpen(false)
                                            }}
                                        >
                                            <LogOut className="mr-2 h-4 w-4" />
                                            {t('auth.logout')}
                                        </Button>
                                    </>
                                ) : isHydrated ? (
                                    <>
                                        <Button asChild size="lg" className="w-full">
                                            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                                                {t('navigation.getStarted')}
                                            </Link>
                                        </Button>
                                        <Button asChild variant="outline" size="lg" className="w-full">
                                            <Link to="/$lang/account" params={{ lang }} onClick={() => setIsMobileMenuOpen(false)}>
                                                {t('navigation.account')}
                                            </Link>
                                        </Button>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </header>
    )
}
